import type { ContactsFilter } from "@/legacy/types/companyFilter";

function nonEmptyStringArray(
  values: readonly (string | null)[] | null | undefined
): string[] | undefined {
  const cleaned = (values ?? []).filter((v): v is string => v != null);
  return cleaned.length > 0 ? cleaned : undefined;
}

function buildIncludeExclude(
  value: { include?: string[]; exclude?: string[] } | null | undefined
) {
  const include = nonEmptyStringArray(value?.include);
  const exclude = nonEmptyStringArray(value?.exclude);
  if (!include && !exclude) return undefined;
  return {
    ...(include ? { include } : {}),
    ...(exclude ? { exclude } : {}),
  };
}

/**
 * The `contactFilters` object sent to the backend, built exactly like OppGen's
 * `buildContactFilters()` (piq-customer-ui `utils/spog/opgen.ts`): empty
 * criteria are dropped, the UI-only `mode` never leaves the client, and the
 * whole object is `undefined` when nothing is selected.
 */
export function buildContactFiltersForExport(
  contactFilters: ContactsFilter | null | undefined
): ContactsFilter | undefined {
  const jobSeniority = buildIncludeExclude(contactFilters?.jobSeniority);
  const jobFunctions = buildIncludeExclude(contactFilters?.jobFunctions);
  const jobTitleKeywordsBase = buildIncludeExclude(
    contactFilters?.jobTitleKeywords
  );
  const jobTitleKeywords =
    jobTitleKeywordsBase &&
    contactFilters?.jobTitleKeywords?.inclusionMethod === "ALL_PRESENT"
      ? { ...jobTitleKeywordsBase, inclusionMethod: "ALL_PRESENT" as const }
      : jobTitleKeywordsBase;

  const locationSource = contactFilters?.location;
  const locationCountryAlpha2s = nonEmptyStringArray(
    locationSource?.countryAlpha2s
  );
  const locationRegionNames = nonEmptyStringArray(locationSource?.regionNames);
  const locationStateIds = nonEmptyStringArray(locationSource?.stateIds);
  const locationExcludedCountryAlpha2s = nonEmptyStringArray(
    locationSource?.excludedCountryAlpha2s
  );
  const location =
    locationCountryAlpha2s ||
    locationRegionNames ||
    locationStateIds ||
    locationExcludedCountryAlpha2s
      ? {
          ...(locationCountryAlpha2s
            ? { countryAlpha2s: locationCountryAlpha2s }
            : {}),
          ...(locationRegionNames ? { regionNames: locationRegionNames } : {}),
          ...(locationStateIds ? { stateIds: locationStateIds } : {}),
          ...(locationExcludedCountryAlpha2s
            ? { excludedCountryAlpha2s: locationExcludedCountryAlpha2s }
            : {}),
        }
      : undefined;

  const timeInRoleBuckets = nonEmptyStringArray(
    contactFilters?.timeInRoleBuckets
  );

  const primaryCompanyOnly = contactFilters?.primaryCompanyOnly;

  if (
    !jobSeniority &&
    !jobFunctions &&
    !jobTitleKeywords &&
    !location &&
    !timeInRoleBuckets &&
    !primaryCompanyOnly
  ) {
    return undefined;
  }

  return {
    ...(jobSeniority ? { jobSeniority } : {}),
    ...(jobFunctions ? { jobFunctions } : {}),
    ...(jobTitleKeywords ? { jobTitleKeywords } : {}),
    ...(location ? { location } : {}),
    ...(timeInRoleBuckets ? { timeInRoleBuckets } : {}),
    ...(primaryCompanyOnly ? { primaryCompanyOnly } : {}),
  };
}

/**
 * "Filter company list" (the default, as in OppGen) also puts the contact
 * criteria into the company filters, so only companies with matching
 * contacts are exported; "Export contacts only" scopes them to the contact
 * sheets.
 */
export function contactFiltersNarrowCompanies(
  contactFilters: ContactsFilter | null | undefined
): boolean {
  return (
    (contactFilters?.mode ?? "filter_company_list") === "filter_company_list"
  );
}
